import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { adminCopy } from '@/copy'
import { Button } from '@/ui/Button/Button'
import { CheckboxField, Field, Input, Select } from '@/ui/Field/Field'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { DEFAULT_ROUTER_MODEL } from '../../constants/models'
import type { RouterConfig, SystemProviderInfo } from '../../types/api'

export function RouterConfigPanel({
  current,
  isSaving,
  onSave,
  onCancel,
}: {
  current?: RouterConfig
  isSaving: boolean
  onSave: (config: RouterConfig | null) => void
  onCancel: () => void
}) {
  const id = useId()
  const [enabled, setEnabled] = useState(!!current)
  const [model, setModel] = useState(current?.model ?? DEFAULT_ROUTER_MODEL)
  const [providerId, setProviderId] = useState(current?.provider_id ?? '')

  const { data: providers = [], isError } = useQuery<SystemProviderInfo[]>({
    queryKey: ['admin', 'providers'],
    queryFn: () =>
      import('../../api/agents').then((m) => m.providersApi.list()),
    staleTime: 60_000,
  })

  function handleSave() {
    if (!enabled) {
      onSave(null)
      return
    }
    if (!model.trim() || !providerId) return
    onSave({ model: model.trim(), provider_id: providerId })
  }

  return (
    <Stack gap="space-16">
      <CheckboxField
        id={`${id}-enabled`}
        label={adminCopy.router.enable}
        checked={enabled}
        onChecked={setEnabled}
      />
      {enabled && (
        <>
          <Field
            label={adminCopy.router.provider}
            error={isError ? adminCopy.loadFailed.providers : undefined}
          >
            <Select
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
            >
              <option value="">{adminCopy.router.select}</option>
              {providers.map((p) => (
                <option key={p.provider_id} value={p.provider_id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={adminCopy.router.model}>
            <Input
              mono
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </Field>
        </>
      )}
      <Inline gap="space-8">
        <Button
          variant="primary"
          disabled={enabled && (!model.trim() || !providerId)}
          loading={isSaving}
          onClick={handleSave}
        >
          {adminCopy.router.save}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          {adminCopy.router.cancel}
        </Button>
      </Inline>
    </Stack>
  )
}
